import React, { useCallback, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ShareIcon from "@mui/icons-material/Share";
import { Images } from "../../../utils/Images";
import ShareLink from "../ShareLink";
import { getCampaignShareDetails } from "../../../api/services/campaignService";
import { getCampaignUrl } from "../../../utils/slugUtils";

const ReadMoreButton = styled(Button)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "700",
  fontSize: isMobile ? "12px" : "14px",
  textTransform: "none",
  backgroundColor: "#1D1D1B",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: isMobile ? "8px 16px" : "13px 19px",
  minWidth: isMobile ? "80px" : "100px",
  height: isMobile ? "32px" : "40px",
  transition: "all 0.2s ease",
  boxShadow: "0px 4px 0px 0px #B9B9B8",
  "&:hover": {
    backgroundColor: "#1D1D1B",
    color: "#FFFFFF",
    // transform: 'translateY(-1px)',
    // boxShadow: "0px 4px 0px 0px #B9B9B8",
  },
  "&:active": {
    transform: "translateY(0px)",
  },
}));

const ReferFriend = ({ campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  
  const {
    id,
    image,
    title,
    description,
    date,
    buttonText = "Share now",
    campaign_type = false,
  } = campaignData || {};

  const fallbackShareUrl =
    (typeof window !== "undefined" && window.location?.origin && campaignData
      ? (() => {
          const url = getCampaignUrl(campaignData);
          return url ? `${window.location.origin}${url}` : "";
        })()
      : "") || "";

  const fetchShareDetails = useCallback(async () => {
    if (!id) return;
    setShareLoading(true);
    setShareError(null);
    try {
      const data = await getCampaignShareDetails(id);
      setShareInfo(data);
    } catch (error) {
      console.error("Failed to fetch share details:", error);
      setShareError("Unable to load share options right now.");
    } finally {
      setShareLoading(false);
    }
  }, [id]);

  const handleShareClick = () => {
    if (!shareInfo && !shareLoading) {
      fetchShareDetails();
    }
    setShowShareDialog(true);
  };

  const handleCloseShareDialog = () => {
    setShowShareDialog(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#956DC4",
        borderRadius: "16px",
        padding: isMobile ? "24px 20px" : " 40px",
        textAlign: "left",
        border: "1px solid #E2E8F0",
        // maxWidth: "1360px",
        width: "100%",
        margin: "0 auto",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        display: "flex",
        position:"relative",
        overflow:"hidden",
      }}
    >
      <Box sx={{ 
        maxWidth: isMobile ? "100%" : "60%",
        position:"relative",
        zIndex: 2, // Ensure content is above decorative elements
        "@media (max-width: 1200px)": {
               maxWidth: isMobile ? "100%" : "40%",
               width:"100%"
            },
       }}>
        {/* Title */}
        <Typography
          variant="h4"
          component="h2"
          sx={{
            display: "flex",
            gap: "10px",
            fontSize: isMobile ? "20px" : "24px",
            fontWeight: "700",
            mb: isMobile ? "16px" : "20px",
            color: "#fff",
            justifyContent:isMobile ? "center" : "start",
          }}
        >
          <img src={Images.streamlineSharpStarWhite} alt="Images.streamlineSharpStarWhite" 
          style={{display:isMobile ? "none" :"block"}} />
           Refer a friend
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: "#fff",
            fontSize: isTablet ? "12px" : "14px",
            lineHeight: "1.6",
            marginBottom: "20px",
            fontWeight: "500",
            textAlign:isMobile ? "center" : "left",
            mx:isMobile ? "auto" : "0",
          }}
        >
          Be the Reason for Someone’s Smile <br style={{display:isMobile ? "block" : "none"}} /> Share True Hope with your friends.
          Every new supporter you bring helps change a life!
        </Typography>

        {/* Share Button */}
        <ReadMoreButton
          isMobile={isMobile}
          aria-label={`Share now  ${title}`}
          onClick={handleShareClick}
          sx={{
            display:"flex",
            justifyContent:isMobile ? "center" : "start", 
            mx:isMobile ? "auto" : "0",
            width:"fit-content",
            position: "relative",
            zIndex: 10, // Ensure button is above decorative elements
          }}
        >
          Share now
        </ReadMoreButton>
         <Box
                component="img"
                src={Images.whiteHearts}
                alt=""
                sx={{
                  position: 'absolute',
                  top: { xs: '20px',sm: '0px', md: '0px' },
                  right: { xs: '20px',sm:"-30px",lg:"-70px", md: '-70px' },
                  width: { xs: '40px', sm: '50px', md: '48px' },
                  height: 'auto',
                  transform: 'rotate(75deg)',
                  zIndex: 1,
                  pointerEvents: 'none', // Prevent blocking clicks
                }}
              />
              <Box
                component="img"
                src={Images.yellowHearts}
                alt=""
                sx={{
                  position: 'absolute',
                  bottom: { xs: '120px', sm: '-35px', md: '-35px' },
                  left: { xs: '20px',sm: '150px', md: '200px',lg:"350px" },
                  width: { xs: '40px', sm: '50px', md: '48px' },
                  height: 'auto',
                  transform: isMobile ? "scaleX(-1) rotate(10deg)" : 'scaleX(-1) rotate(40deg)',
                  zIndex: 1,
                  pointerEvents: 'none', // Prevent blocking clicks
                }}
              />
              <Box
                component="img"
                src={Images.stringHand}
                alt=""
                sx={{
                  position: 'absolute',
                  bottom: { xs: '-30px', sm: '-35px', md: '-35px' },
                  right: { xs: '20px',sm: '150px', md: '200px',lg:"350px" },
                  width: { xs: '40px', sm: '50px', md: '48px' },
                  height: 'auto',
                  transform: isMobile ? "scaleX(-1) rotateZ(60deg)" : 'scaleX(-1) rotate(40deg)',
                  zIndex: 1,
                  pointerEvents: 'none', // Prevent blocking clicks
                  "@media (min-width: 601px)": {
                    display:"none"
                  },
                }}
              />
      </Box>
      
      <Box sx={{
        position:"absolute", 
        right:"105px", 
        top:isMobile?"0px":"50%",
        transform:isMobile?"translateY(0%)":"translateY(-50%)",
        maxWidth: "667px",
        pointerEvents: 'none', // Prevent blocking clicks
        zIndex: 1,
        "@media (max-width: 1400px)": {
              right:"70px"
            },
            "@media (max-width: 1200px)": {
              right:"50px"
            },
            "@media (max-width: 900px)": {
              right:"30px"
            },
            "@media (max-width: 600px)": {
              left:"50%",
              right:"unset",
              top:"20px",
              transform:"translateX(-50%)"
            },
      }}>
        <Box sx={{position:"relative", width: "342px", height: "342px", display:"flex", justifyContent:"center", alignItems:"center",
           backgroundImage: `url(${Images.requestFriendImgBg})`,
        }}>
          <Box
            component="img"
            src={Images.requestFriendImg}
            alt="Images.requestFriendImg"
            sx={{
              width: "178px", height: "174px",display:isMobile ? "none" :"block",
             
            }}
          />
        </Box>
      </Box>

      {/* Share Dialog */}
      <Dialog
        open={showShareDialog}
        onClose={handleCloseShareDialog}
        maxWidth={isMobile ? "xs" : "md"}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            margin: isMobile ? '16px' : 'auto',
            maxHeight: isMobile ? 'calc(100vh - 32px)' : '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(isMobile && {
              width: 'calc(100% - 32px)',
              maxWidth: '100%',
            }),
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <ShareLink
          shareData={shareInfo}
          fallbackUrl={fallbackShareUrl}
          isLoading={shareLoading}
          error={shareError}
          onRetry={fetchShareDetails}
          onClose={handleCloseShareDialog}
        />
      </Dialog>
    </Box>
  );
};

export default ReferFriend;
